import type { RefObject } from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import FormHelperText from '@mui/material/FormHelperText'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { LineOfInquiry } from '../types'
import {
  LOI_EDIT_LIMITS,
  loiDescriptionFieldError,
  loiTitleFieldError,
} from './LoiCardEdit.validation'
import {
  loiCardBorderColor,
  loiCardInactiveSurface,
  loiCardPrimaryInnerBg,
  loiCardPrimaryOuterBg,
  loiInstrumentTagChipRowSx,
  loiRankBadgeChipSx,
} from './loiFigmaLoiCardStyles'
import {
  loiEditDeleteToolbarIconSrc,
  loiEditResetToolbarIconSrc,
  loiEditSaveToolbarIconSrc,
  loiEditToolbarIconButtonSx,
  loiEditToolbarIconImgSx,
} from './loiCardEditToolbarIcons'

export { LOI_TITLE_PATTERN } from './LoiCardEdit.validation'

const { TITLE_MAX, DESC_MAX } = LOI_EDIT_LIMITS

export type LoiCardEditProps = {
  readonly surfaceRef?: RefObject<HTMLDivElement | null>
  readonly loi: LineOfInquiry
  readonly rankIndex: number
  readonly title: string
  readonly description: string
  readonly onChangeTitle: (v: string) => void
  readonly onChangeDescription: (v: string) => void
  readonly onSave: () => void
  readonly onReset: () => void
  readonly onDeleteClick: () => void
  readonly saveDisabled: boolean
  readonly resetDisabled: boolean
  readonly deleteDisabled: boolean
  readonly saveTooltip: string
  readonly resetTooltip: string
  readonly titleAuxError?: string
  readonly isSaving: boolean
  readonly clickAwayMessage: string | null
}

export function LoiCardEdit({
  surfaceRef,
  loi,
  rankIndex,
  title,
  description,
  onChangeTitle,
  onChangeDescription,
  onSave,
  onReset,
  onDeleteClick,
  saveDisabled,
  resetDisabled,
  deleteDisabled,
  saveTooltip,
  resetTooltip,
  titleAuxError = '',
  isSaving,
  clickAwayMessage,
}: LoiCardEditProps) {
  const isPrimary = rankIndex === 0
  const titleLen = title.length
  const descLen = description.length
  const titleErr = loiTitleFieldError(title) || titleAuxError
  const descErr = loiDescriptionFieldError(description)

  return (
    <Box ref={surfaceRef} data-testid={`loi-card-edit-${loi.id}`}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: '8px',
          borderColor: loiCardBorderColor,
          bgcolor: isPrimary ? loiCardPrimaryOuterBg : loiCardInactiveSurface,
          boxShadow: '0px 1px 1px rgba(0,0,0,0.05)',
        }}
      >
        <CardContent
          sx={{
            p: 2.5,
            '&:last-child': { pb: 2.5 },
            bgcolor: isPrimary ? loiCardPrimaryInnerBg : 'transparent',
          }}
        >
          {clickAwayMessage ? (
            <Typography
              role="alert"
              sx={{ fontSize: 13, color: 'error.main', mb: 1.5 }}
            >
              {clickAwayMessage}
            </Typography>
          ) : null}

          <Box
            sx={{
              mb: 2,
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gridTemplateRows: 'auto auto',
              columnGap: 1,
              rowGap: 0.25,
            }}
          >
            <TextField
              hiddenLabel
              value={title}
              onChange={(e) => onChangeTitle(e.target.value)}
              fullWidth
              size="small"
              error={Boolean(titleErr)}
              inputProps={{
                maxLength: TITLE_MAX,
                'aria-label': 'Line of inquiry title',
                'aria-describedby': `loi-title-helper-${loi.id}`,
              }}
              sx={{
                gridColumn: 1,
                gridRow: 1,
                minWidth: 0,
                '& .MuiInputBase-input': { fontWeight: 700 },
              }}
            />
            <FormHelperText
              id={`loi-title-helper-${loi.id}`}
              error={Boolean(titleErr)}
              sx={{
                gridColumn: 1,
                gridRow: 2,
                textAlign: 'right',
                mx: 0,
              }}
            >
              {titleErr || `${titleLen}/${TITLE_MAX}`}
            </FormHelperText>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                gridColumn: 2,
                gridRow: 1,
                alignSelf: 'center',
                flexShrink: 0,
              }}
            >
              <Chip
                size="small"
                label={isPrimary ? 'Primary' : 'Up Next'}
                sx={loiRankBadgeChipSx(isPrimary)}
              />
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ flexShrink: 0 }}
              >
              <Tooltip title={resetTooltip}>
                <span>
                  <IconButton
                    size="small"
                    aria-label="Reset line of inquiry edits"
                    onClick={onReset}
                    disabled={resetDisabled || isSaving}
                    data-testid={`loi-reset-${loi.id}`}
                    sx={loiEditToolbarIconButtonSx}
                  >
                    <Box
                      component="img"
                      src={loiEditResetToolbarIconSrc}
                      alt=""
                      sx={loiEditToolbarIconImgSx}
                    />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={saveTooltip}>
                <span>
                  <IconButton
                    size="small"
                    aria-label="Save line of inquiry"
                    onClick={onSave}
                    disabled={saveDisabled || isSaving}
                    data-testid={`loi-save-${loi.id}`}
                    sx={loiEditToolbarIconButtonSx}
                  >
                    <Box
                      component="img"
                      src={loiEditSaveToolbarIconSrc}
                      alt=""
                      sx={loiEditToolbarIconImgSx}
                    />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={deleteDisabled ? 'At least one LOI is required' : 'Delete'}
              >
                <span>
                  <IconButton
                    size="small"
                    aria-label="Delete line of inquiry"
                    onClick={onDeleteClick}
                    disabled={deleteDisabled || isSaving}
                    data-testid={`loi-delete-open-${loi.id}`}
                    sx={loiEditToolbarIconButtonSx}
                  >
                    <Box
                      component="img"
                      src={loiEditDeleteToolbarIconSrc}
                      alt=""
                      sx={loiEditToolbarIconImgSx}
                    />
                  </IconButton>
                </span>
              </Tooltip>
              </Stack>
            </Stack>
          </Box>

          <TextField
            hiddenLabel
            value={description}
            onChange={(e) => onChangeDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            size="small"
            error={Boolean(descErr)}
            helperText={
              descErr ||
              `${descLen}/${DESC_MAX}`
            }
            FormHelperTextProps={{
              sx: descErr
                ? undefined
                : {
                    textAlign: 'right',
                    mx: 0,
                  },
            }}
            inputProps={{
              maxLength: DESC_MAX,
              'aria-label': 'Line of inquiry description',
            }}
            sx={{
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: 'primary.main',
                },
            }}
          />

          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1}
            sx={{ mt: 2, pt: 1 }}
          >
            {loi.instrumentTags.map((tag) => (
              <Chip
                key={`${loi.id}-edit-${tag}`}
                label={tag}
                size="small"
                sx={loiInstrumentTagChipRowSx(isPrimary)}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}
